import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { ICustomOverridePage } from "../models/pages";
import { DomSanitizer } from "@angular/platform-browser";

@Injectable({
	providedIn: 'root'
})
export class CustomizationService {

	private widgetConfigData : any;
	public widgetScriptLoaded$: BehaviorSubject<boolean>;
	public widgetIsShown$: BehaviorSubject<boolean>;

	constructor(private _http: HttpClient,
              private sanitizer: DomSanitizer) {
		this.widgetScriptLoaded$ = new BehaviorSubject(false);
		this.widgetIsShown$ = new BehaviorSubject(false);
	}

	/**
	 * @api {get} /v2/Customizations/Logo Get Rapidship Logo
	 * @apiName fetchRapidshipLogo
	 * @apiGroup Customizations
	 * @apiVersion 20.1.0
	 */
	fetchRapidshipLogo(siteId) {
		let params: HttpParams = new HttpParams();
		if (siteId !== null) {
			params = params.append('siteId', siteId);
		}
		return this._http.get<any>(`${environment.apiUrl}/v2/Customizations/Logo`, {params});
	}

	/**
	 * @api {get} /v2/Customizations/Translations Get Customized Translations
	 * @apiName getCustomizedTranslations
	 * @apiGroup Customizations
	 * @apiVersion 20.1.0
	 */
	getCustomizedTranslations(siteId) {
		let params: HttpParams = new HttpParams();
		if (siteId !== null) {
			params = params.append('siteId', siteId);
		}
		return this._http.get<any>(`${environment.apiUrl}/v2/Customizations/Translations`, {params});
	}

	/**
	 * @api {get} /v2/Customizations/Styles Get Customized Style Variables
	 * @apiName getStyleVariables
	 * @apiGroup Customizations
	 * @apiVersion 20.1.0
	 */
	getStyleVariables(siteId) {
		let params: HttpParams = new HttpParams();
		if (siteId !== null) {
			params = params.append('siteId', siteId);
		}
		return this._http.get(`${environment.apiUrl}/v2/Customizations/Styles`, {responseType: 'text', params});
	}

	/**
	 * @api {get} /v2/Customizations/Styles Get Customized Style Variables
	 * @apiName getStyleVariables
	 * @apiGroup Customizations
	 * @apiVersion 20.1.0
	 */
	getCustomWidgets(siteId) {
		let params: HttpParams = new HttpParams();
		if (siteId !== null) {
			params = params.append('siteId', siteId);
		}
		return this._http.get(`${environment.apiUrl}/v2/Customizations/Widgets`, {params});
	}

	/**
	 * @api {get} /v2/Customizations Retrieve list of available customization pages
	 * @apiName Customizations/Pages
	 * @apiGroup XdUsers
	 * @apiVersion 21.0.0
	 */
	getRapidShipCustomPages(siteId): Observable<ICustomOverridePage[]> {
		let params: HttpParams = new HttpParams();
		if (siteId !== null) {
			params = params.append('siteId', siteId);
		}
		return this._http.get<ICustomOverridePage[]>(`${environment.apiUrl}/v2/Customizations/Pages`, {params});
	}

	saveWidgetConfig(config) : any {
		this.widgetConfigData = this.correctWidgetConfig(config);
		return this.widgetConfigData;
	}

	retrieveWidgetConfig() {
		return this.widgetConfigData;
	}

	correctWidgetConfig(config) : any {
		let scriptText: string = config?.loadScript;
		scriptText = scriptText?.replace(/\n/g, ' '); // remove newlines
		let scriptMatches: RegExpMatchArray = scriptText?.match(/<script(>|.*?[^?]>)/gm);

		// Determine if the incoming script already has a <script> tag in it. This shouldn't be included
		// If it is included, we will strip it away.
		if (scriptMatches) {
			const scriptTag: string = scriptMatches[0];
			const regex = /src\s*=\s*['"](\S*)['"]/gm;
			let sourceMatches = (scriptTag?.match(regex) || []).map((e: string) => e.replace(regex, '$1'));

			// Check to see if user is sending in a full script tag using a src URL. If so, we can simply reference URL source.
			if (sourceMatches && sourceMatches.length > 0) {
				let source = sourceMatches[0];
				if (source){
					config.loadScript = source;
					config.scriptType = 'URL_REFERENCE';
				}
			} else {
				scriptText = scriptText?.replace(/<noscript>.*<\/noscript>/gm, ''); // remove noscript
				scriptText = scriptText?.replace(scriptTag, ''); // remove opening script tag
				scriptText = scriptText?.replace('</script>', ''); // remove closing script tag

				config.loadScript = scriptText;
				config.scriptType = 'SCRIPT_BLOCK';
			}
		}

		return config;
	}

  updateUrlForHttp(url: string): string {
    if (url.includes('http')) {
      return <string>this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    return <string>this.sanitizer.bypassSecurityTrustResourceUrl('https://' + url);
  }
}
